import React, {useContext, useEffect, useReducer, useRef, useState} from "react";
import Context from "../../context/Message";
import LoadMoreButton from "../../components/Message/LoadMoreButton";
import Messages from "../../components/Message/Messages";

const updateMessage = (messages, message) => {
    let collection = messages;
    if (message.parentId) {
        collection = messages.find(({id}) => id === message.parentId)?.children;
        if (!collection) {
            console.warn('parent message not found for', message);
            return messages;
        }
    }
    let index = collection.findIndex(({id}) => id === message.id);
    if (-1 === index) {
        collection.push(message);
    } else {
        if (message.children && message.children.length === 0) {
            delete message.children;
        }
        collection[index] = {...collection[index], ...message};
    }
    return messages;
};
const deleteMessage = (messages, {id}) => {
    let collection = messages;
    const allMessages = messages.reduce((messages, message) => {
        return messages.concat(message, message.children);
    }, []);
    const message = allMessages.find((message) => message?.id === id);
    if (!message) {
        console.warn('Cant find message to delete', id);
        return messages;
    }
    if (message.parentId) {
        const parent = messages.find(({id}) => id === message.parentId);
        if (!parent) {
            console.warn('Parent message not found for delete', id);
            return messages;
        }
        collection = parent.children;
    }
    const index = collection.findIndex(({id}) => id === message.id);
    if (-1 === index) {
        console.warn('Message not found', message);
        return messages;
    }
    collection.splice(index, 1);

    return messages;
};

const reducer = (state, action) => {
    state = state.slice();
    switch(action.type) {
        case 'concat':
            return state.concat(
                action.messages.filter(({id}) =>
                    -1 === state.findIndex(m => m.id === id)
                )
            );
        case 'clear':
            return [];
        case 'create':
        case 'update':
            return action.messages.reduce(updateMessage, state);
        case 'delete':
            return action.messages.reduce(deleteMessage, state);
        default:
            console.error(new Error('Unknown reduce action type'));
            return state;
    }
};

export default ({
    comet,
    uiTab,
    position,
    messageLimit,
    filter,
    episode,
    currentStream,
    currentRoom,
    onGradientToggle,
}) => {
    const {
        $playerNotification,
        name,
        client,
        fetchMessages,
    } = useContext(Context);
    const [messages, dispatchMessages] = useReducer(reducer, []);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const messagesContainerRef = useRef(null);

    const onCreateMessage = (message) => {
        dispatchMessages({type: 'create', messages: [message]});
        if (message.parentId) {
            return;
        }

        setTimeout(() => {
            if (client && client.id === (message.clientRegistration?.id ?? message.client?.id)) {
                scrollToMessage(message);
            } else {
                scrollToEnd();
            }
        }, 100);
    }
    const onUpdateMessage = (message) => dispatchMessages({type: 'update', messages: [message]});
    const onDeleteMessage = (message) => dispatchMessages({type: 'delete', messages: [message]});
    const onClearMessages = () => dispatchMessages({type: 'clear'});
    const onSwitchFromMessage = (prevName, message) => {
        if (client.id && client.id === (message.clientRegistration?.id ?? message.client?.id)) {
            let text = 'Ваш вопрос перенесён в комментарии';
            if (name === 'question') {
                text = 'Ваш комментарий перенесён в вопросы';
            }
            $playerNotification.showNotification(text, message.text);
        }
        onCreateMessage(message);
    }

    useEffect(() => {
        fetch()
            .then(() => {
                scrollToEnd(true);
            })
        ;

        comet
            .on(`create:${name}/message`, onCreateMessage)
            .on(`update:${name}/message`, onUpdateMessage)
            .on(`delete:${name}/message`, onDeleteMessage)
            .on(`clear:${name}/message`, onClearMessages)
            .on(`create-from-question:${name}/message`, (message) => onSwitchFromMessage('question', message))
            .on(`create-from-comment:${name}/message`, (message) => onSwitchFromMessage('comment', message))
        ;

        uiTab
            .on('open', () => setTimeout(() => scrollToEnd(true), 200))
        ;
    }, []);

    useEffect(() => {
        setTimeout(() => scrollToEnd(true), 200);
    }, [currentStream]);

    useEffect(() => {
        const handleScroll = () => {
            if (!onGradientToggle) {
                return;
            }
            if(getScrollPosition() > 5) {
                onGradientToggle(true);
            } else {
                onGradientToggle(false);
            }
        };

        if (messagesContainerRef.current) {
            messagesContainerRef.current.addEventListener('scroll', handleScroll, false);
        }
    }, [messagesContainerRef]);

    const fetch = () => {
        const params = {
            limit: messageLimit,
        };
        if (episode) {
            params.episodeKey = episode.key;
        }

        setLoading(true);
        return fetchMessages(params)
            .then((loadedMessages) => {
                dispatchMessages({type: 'concat', messages: loadedMessages});
                if (loadedMessages?.length < messageLimit) {
                    setHasMore(false);
                }
            })
            .finally(() => setLoading(false))
        ;
    };

    const scrollTopSmooth = (element, scrollTop) => {
        if (!element.scroll) {
            return element.scrollTop = scrollTop;
        }
        element.scroll({
            top: scrollTop,
            left: 0,
            behavior: 'smooth',
        });
    };

    const scrollToEnd = (force) => {
        const container = messagesContainerRef.current;
        if (!container) {
            return;
        }
        const sideModule = position === 'right';
        const lastMessage = sideModule
            ? container.querySelector('ul:first-of-type li:last-of-type')
            : container.querySelector('ul:first-of-type li:first-of-type');
        if (!lastMessage) {
            return console.warn('Cant find last message to scroll');
        }
        const scrollPosition = getScrollPosition();
        let minScrollThreshold = 80;
        minScrollThreshold += position === 'right' ? lastMessage.offsetHeight : 0;
        if(force || scrollPosition < minScrollThreshold) {
            scrollTopSmooth(container, sideModule ? container.scrollHeight - container.offsetHeight : 0);
        }
    };

    const scrollToMessage = (message) => {
        const container = messagesContainerRef.current;
        if (!container) {
            return;
        }
        const messageElement = container.querySelector(`#${name}_${message.id}`);
        if (!messageElement) {
            return console.log('Cant find message element for scroll', message, `#${name}_${message.id}`    );
        }
        scrollTopSmooth(container, messageElement.offsetTop)
    };

    const getScrollPosition = (reverse) => {
        if (!messagesContainerRef.current) {
            return;
        }
        const list = messagesContainerRef.current;
        if(reverse ^ position === 'right') {
            return list.scrollHeight - (list.scrollTop + list.offsetHeight);
        }
        return list.scrollTop;
    }

    let filteredMessages = messages
        .filter(message => {
            if (!message.moderate && (!client || client?.id !== (message.clientRegistration?.id ?? message.client?.id))) {
                return false;
            }
            if (episode) {
                return message.episodeId === episode.id
                    || (message.time > episode.timeStart && message.time < episode.timeStop);
            }
            if (filter === 'stream') {
                return currentStream && currentRoom && message.roomId && message.roomId === currentRoom.id;
            }
            return true;
        })
    ;

    return (
        <div className={`${name}Messages messages`} ref={messagesContainerRef}>
            {position === 'right' ?
                <LoadMoreButton hasMore={hasMore} loading={loading} onClick={fetch} />
                : null}
            <Messages messages={filteredMessages} />
            {position === 'bottom' ?
                <LoadMoreButton hasMore={hasMore} loading={loading} onClick={fetch} />
                : null}
        </div>
    );
};
